import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState
} from 'react';
import fetchAPI from '../../../utils/fetchAPI';
import useTranslations from '../../../hooks/useTranslations';
import AdminGroupWizardView from '../view/AdminGroupWizardView';
import { CollaboratorsContext } from './FormBuilderCollaborateEngine';
import generateId from '../../../utils/generateId';
import useGlobalVariables from '../../../hooks/useGlobalVariables';
import {
  existsInMap,
  formatGroupIds,
  initializeSections,
  joinAllFields
} from '../../../utils/adminGroupWizardUtils';

const WizardReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };
    case 'update_permissions':
      return {
        ...state,
        permissions: { ...state.permissions, ...payload }
      };
    default:
      return state;
  }
};

const initialEmptyState = {
  id: '',
  name: '',
  userIds: [],
  permissions: {
    groupIds: [''],
    fieldIds: [],
    query: ''
  },
  queryFilters: {
    filters: []
  }
};

/**
 * @param {Array} groupOptions - All the user groups that exist in the tenant
 * @param {Object} adminGroupData - Data to be displayed in the wizard
 * @param {Object} bucketNames - useRef that holds an object in the format of: {bucketId: bucketName}
 * @param {Object} bucketFields - useRef that holds an object in the format of: {bucketId: [fieldIds]}
 * @param {Array} collaborators - holds all the admins that are displayed in the main list (form admins + section admins)
 * @param {Function} closeWizard - handles the closing of the wizard
 */
const AdminGroupWizardEngine = ({
  groupOptions = [],
  adminGroupData = initialEmptyState,
  bucketNames,
  bucketFields,
  collaborators,
  closeWizard = () => {}
}) => {
  const { form } = useContext(CollaboratorsContext);
  const [data, dispatch] = useReducer(WizardReducer, initialEmptyState);
  const initialQuery =
    adminGroupData &&
    adminGroupData.permissions &&
    adminGroupData.permissions.query
      ? adminGroupData.permissions.query
      : [];

  const [query, setQuery] = useState(initialQuery);
  const [adminFieldTitles, setAdminFieldTitles] = useState([]);
  const [chosenFields, setChosenFields] = useState([]);
  const [sectionsMap, setSectionsMap] = useState({});

  const t = useTranslations();
  const { setIsLoading } = useGlobalVariables();

  useEffect(() => {
    //init
    if (
      groupOptions.length !== 0 &&
      groupOptions[0].text != 'All user groups'
    ) {
      groupOptions.unshift({
        text: 'All user groups',
        label: '',
        value: ''
      });
    }

    const joinedFields = joinAllFields(form.adminFields, form.fields);
    const initMap = initializeSections(joinedFields);
    setSectionsMap(initMap);

    //setting the array of field titles and ids
    const titles = [];
    joinedFields.forEach(f => {
      if (existsInMap(f.internalId, initMap)) {
        titles.push({
          text: f.title,
          value: f.internalId,
          disabled: f.uiType !== 'section_break',
          $customCSS:
            f.uiType === 'section_break'
              ? '&&{font-weight: bold;}'
              : '&&{margin-left: 1rem;}'
        });
      }
    });
    setAdminFieldTitles(titles);

    if (adminGroupData && adminGroupData.id !== undefined) {
      setChosenFields(adminGroupData.permissions.fieldIds);
      dispatch({ type: 'update', payload: adminGroupData });
    }
  }, []);

  const onGroupNameChanged = useCallback(event => {
    dispatch({
      type: 'update',
      payload: {
        name: event.target.value
      }
    });
  }, []);

  const onGroupsChanged = useCallback(groups => {
    let groupIds = [];

    if (!groups.length || groups[groups.length - 1] === '') {
      groupIds = [''];
    } else if (groups.includes('') && groups.length > 1) {
      groupIds = groups.filter(group => group !== '');
    } else {
      groupIds = groups;
    }

    dispatch({
      type: 'update_permissions',
      payload: {
        groupIds: groupIds
      }
    });
  }, []);

  const onUsersChanged = useCallback(users => {
    dispatch({
      type: 'update',
      payload: {
        userIds: users
      }
    });
  }, []);

  const onFieldsChanged = useCallback(
    fields => {
      //checks if there was a field removed
      let diff = chosenFields.length
        ? chosenFields.filter(x => !fields.includes(x))
        : [];
      let removeFields = [];
      let selectedFields = [];
      let updatedFields = chosenFields;

      if (diff.length) {
        diff.forEach(uncheckedField => {
          if (sectionsMap[uncheckedField]) {
            removeFields = sectionsMap[uncheckedField];
            removeFields.unshift(uncheckedField);
          } else {
            removeFields = diff;
          }
        });

        updatedFields = updatedFields.filter(f => {
          return !removeFields.includes(f);
        });
        selectedFields = updatedFields;
      } else {
        fields.forEach(field => {
          selectedFields = chosenFields.concat(field);
          let temp = sectionsMap[field];
          if (sectionsMap[field]) {
            selectedFields = selectedFields.concat(temp);
          }
        });
      }

      //removes duplicates of ids from the array
      const uniqueSelectedFields = selectedFields.filter(
        (item, i, ar) => ar.indexOf(item) === i
      );
      setChosenFields(uniqueSelectedFields);

      dispatch({
        type: 'update_permissions',
        payload: {
          fieldIds: uniqueSelectedFields
        }
      });
    },
    [sectionsMap, chosenFields]
  );

  const onCancel = useCallback(() => {
    closeWizard();
  }, [closeWizard]);

  const updateQuery = q => {
    setQuery(q);
    dispatch({
      type: 'update_permissions',
      payload: {
        query: q
      }
    });
  };

  const updateBucketNames = useCallback(
    bucketId => {
      let names = bucketNames.current;
      names[bucketId] = data.name;
      bucketNames.current = names;
    },
    [data]
  );

  const updateBucketFields = useCallback(
    bucketId => {
      let fields = bucketFields.current;
      fields[bucketId] = chosenFields;
      bucketFields.current = fields;
    },
    [chosenFields]
  );

  const onSave = useCallback(() => {
    const adminResults = [];

    collaborators.forEach(element => {
      if ((element && element.bucketId) !== (data && data.id)) {
        const obj = {
          ...element,
          assetId: element.assetId,
          user_id: element.user_id,
          type: element.type,
          notificationInfo: element.notificationInfo,
          bucketId: element && element.bucketId ? element.bucketId : undefined,
          groupId: element && element.groupId ? element.groupId : undefined,
          viewOnly: element?.viewOnly ? element.viewOnly : undefined
        };
        adminResults.push(obj);
      }
    });

    const userIds = data.userIds.map(user => user.value);
    const groupIds = formatGroupIds(data.permissions.groupIds);
    const bucketId = data && data.id ? data.id : `b${generateId()}`;

    updateBucketNames(bucketId);
    updateBucketFields(bucketId);

    setIsLoading(true);

    let _422Err = false;
    fetchAPI(`/forms/v1/forms/${form.id}/collaborate/bucket/${bucketId}`, {
      method: 'POST',
      body: {
        ...data,
        id: bucketId,
        userIds: userIds,
        queryFilters: {
          filters: adminResults
        },
        permissions: {
          groupIds: groupIds,
          fieldIds: data.permissions.fieldIds,
          query: data.permissions.query
        }
      },
      handleValidationError: e => {
        if (
          e &&
          e.errors[0].code === 'com.vinciworks.infra.missingArrayElements'
        ) {
          _422Err = true;
        }
        reject(e);
      }
    })
      .then(res => {
        openSnackbar('success', 'snackbars.assignees.success');
        closeWizard();
      })
      .catch(e => {
        console.log(e.message);
        if (_422Err) {
          openSnackbar('error', t('api.status_code.422'));
        } else {
          openSnackbar('error', t('snackbars.general.error'));
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [data, collaborators]);

  const isSaveBtnDisabled = useCallback(() => {
    return !(
      data.name &&
      data.userIds &&
      data.userIds.length &&
      data.permissions &&
      data.permissions.fieldIds &&
      data.permissions.fieldIds.length
    );
  }, [data]);

  const filterExistingCollaborators = candidate => {
    return !collaborators.find(one => one.user_id === candidate.value);
  };

  const viewProps = {
    filterExistingCollaborators,
    form,
    data,
    groupName: data.name,
    groupIds: data.permissions && data.permissions.groupIds,
    collaboratorValues: data.userIds,
    query,
    adminFieldTitles,
    groupOptions,
    chosenFields,
    onCancel,
    onGroupsChanged,
    onUsersChanged,
    onFieldsChanged,
    onGroupNameChanged,
    updateQuery,
    onSave,
    isSaveBtnDisabled
  };

  return <AdminGroupWizardView {...viewProps} />;
};

export default AdminGroupWizardEngine;
